<script>
/* Imports */
import { mapActions, mapGetters } from 'vuex';

/* Components */
import Breadcrumbs from '../components/Breadcrumbs.vue';
import VatTooltip from '../components/VatTooltip.vue';
import OldTitles from '../components/OldTitles.vue';
import OldProductAvailability from '../components/OldProductAvailability.vue';
import OldProductFootnote from '../components/OldProductFootnote.vue';
import OldKicker from '../components/OldKicker.vue';
import OldProductInfoPrice from '../components/OldProductInfoPrice.vue';
import OldKlarnaWrapper from '../components/OldKlarnaWrapper.vue';
import OldKlarnaWidget from '../components/OldKlarnaWidget.vue';
import OldCtasWrapper from '../components/OldCtasWrapper.vue';
import OldColorPicker from '../components/OldColorPicker.vue';
import OldEmsFilters from '../components/OldEmsFilters.vue';
import OldAffirmWidget from '../components/OldAffirmWidget.vue';
import OldShippingDisclaimer from '../components/OldShippingDisclaimer.vue';
import ApacWidget from '../components/ApacWidget.vue';
import ProductInfoSkeletonLoader from '../components/ProductInfoSkeletonLoader.vue';
import OldTruemedWidget from '../components/OldTruemedWidget.vue';

/* Composables */
import { useScreenResize } from '../composables/useScreenResize.js';

/* Helpers */
import {
  sanitizeAnalyticsString,
} from '../helpers/ga/analytics.js';
import { breakpoints } from '../helpers/breakpoints.js';

export default {
  name: 'ProductInfo',
  components: {
    Breadcrumbs,
    OldTitles,
    OldKicker,
    OldProductAvailability,
    OldProductInfoPrice,
    OldKlarnaWrapper,
    OldKlarnaWidget,
    OldCtasWrapper,
    OldProductFootnote,
    OldColorPicker,
    OldEmsFilters,
    VatTooltip,
    OldAffirmWidget,
    ApacWidget,
    OldShippingDisclaimer,
    ProductInfoSkeletonLoader,
    OldTruemedWidget,
  },
  setup() {
    const { screenWidth } = useScreenResize();
    return {
      screenWidth,
    };
  },
  computed: {
    ...mapGetters({
      productName: 'getProductName',
      showProductName: 'getShowProductName',
      productVariation: 'getProductVariation',
      showProductVariation: 'getShowProductVariationName',
      partNumber: 'getPartNumber',
      showPartNumber: 'getShowPartNumber',
      showKicker: 'getShowKicker',
      kickerText: 'getKickerText',
      kickerTextNoStyling: 'getKickerTextNoStyling',
      kickerIsDiscontinued: 'getKickerIsDiscontinued',
      priceData: 'getPriceData',
      formattedPrice: 'getFormattedPrice',
      showPriceData: 'getShowPriceData',
      showPriceLoader: 'getShowPriceLoader',
      displayedSalePrice: 'getDisplayedSalePrice',
      displayedSavings: 'getDisplayedSavings',
      shippingDisclaimer: 'getShippingDisclaimer',
      showShippingDisclaimer: 'getShowShippingDisclaimer',
      addToCartLoading: 'getAddToCartLoading',
      showBuyAction: 'getHasBuyAction',
      addToCartHref: 'getBuyActionHref',
      addToCartText: 'getBuyActionText',
      showDealerAction: 'getHasDealerAction',
      dealerActionHref: 'getDealerActionHref',
      dealerActionText: 'getDealerActionText',
      expressWarning: 'getExpressWarning',
      showExpressWarning: 'getShowExpressWarning',
      productHeaderDetails: 'getProductHeaderDetails',
      showProductHeaderDetails: 'getShowProductHeaderDetails',
      productAvailability: 'getProductAvailability',
      showProductAvailability: 'getShowProductAvailability',
      vatToolTip: 'getVatToolTip',
      showVatToolTip: 'getShowVatToolTip',
      productLegalText: 'getProductLegalText',
      showProductLegalText: 'getShowProductLegalText',
      productMarketingSummary: 'getProductMarketingSummary',
      showProductMarketingSummary: 'getShowProductMarketingSummary',
      showAddToCartBtn: 'getShowAddToCartBtn',
      widgetMetadata: 'getWidgetMetadata',
      showAffirmWidget: 'getShowAffirmWidget',
      showKlarnaWidget: 'getShowKlarnaWidget',
      showAPACWidget: 'getShowAPACWidget',
      galleryLoaded: 'getGalleryLoaded',
      loadingPrice: 'getLoadingPrice',
      kickersLoaded: 'getKickersLoaded',
      truemedEligible: 'getTruemedEligible',
      breadcrumbs: 'getBreadCrumbs',
      seoAttributes: 'getSeoAttributes',
    }),
    productPrice() {
      return this.priceData?.salePrice?.price ?? this.priceData?.listPrice?.price ?? '';
    },
    discount() {
      return this.priceData?.savings?.price ?? '';
    },
    currencyCode() {
      return window?.utag_data?.currency_code;
    },
    getShowProductInfo() {
      return this.galleryLoaded && !this.loadingPrice && this.kickersLoaded;
    },
    breadCrumbAnalyticsData() {
      return {
        productName: sanitizeAnalyticsString(this.seoAttributes?.productDisplayName),
        productPrice: this.productPrice,
        discount: this.discount,
        currency: this.currencyCode,
      };
    },
    isDesktopView() {
      return this.screenWidth >= breakpoints['desktop-s'];
    },
  },
  watch: {
    partNumber() {
      this.callInterstitialAccessories();
    },
  },
  mounted() {
    this.callInterstitialAccessories();
  },
  methods: {
    ...mapActions([
      'setAddToCartLoadingState',
      'callInterstitialAccessories',
      'setIsAffirmScriptLoaded',
    ]),
  },
};
</script>

<template>
  <div
    id="js__product__info"
    class="app__product__info"
  >
    <template v-if="getShowProductInfo">
      <Breadcrumbs
        v-if="isDesktopView"
        :crumbs="breadcrumbs"
        :analytics-data="breadCrumbAnalyticsData"
      />
      <OldTitles
        :title="productName"
        :show-product-name="showProductName"
        :subtitle="productVariation"
        :show-subtitle="showProductVariation"
        :part-number="partNumber"
        :show-part-number="showPartNumber"
      />
      <OldKicker
        id="js__product__info__kicker"
        :show-kicker="showKicker"
        :kicker-text="kickerText"
        :kicker-text-no-styling="kickerTextNoStyling"
        :kicker-is-discontinued="kickerIsDiscontinued"
      />
      <OldProductAvailability
        v-if="showProductAvailability"
        :content="productAvailability"
      />
      <OldProductInfoPrice
        :formatted-price="formattedPrice"
        :show-price-loader="showPriceLoader"
        :show-price-data="showPriceData"
        :displayed-sale-price="displayedSalePrice"
        :displayed-savings="displayedSavings"
      />
      <OldKlarnaWrapper
        v-if="showKlarnaWidget"
        id="js__product__info__klarna"
      >
        <OldKlarnaWidget :widget-metadata="widgetMetadata" />
      </OldKlarnaWrapper>
      <OldAffirmWidget
        v-if="showAffirmWidget"
        id="js__product__info__affirm"
        :price-data="priceData"
        :set-is-affirm-script-loaded="setIsAffirmScriptLoaded"
      />
      <OldTruemedWidget
        v-if="truemedEligible"
        id="js__product__info__truemed"
      />
      <VatTooltip
        v-if="showVatToolTip"
        :content="vatToolTip"
      />
      <OldEmsFilters
        :currency-code="currencyCode"
      />
      <OldColorPicker
        :currency-code="currencyCode"
      />
      <OldShippingDisclaimer
        v-if="showShippingDisclaimer"
        :shipping-disclaimer-text="shippingDisclaimer"
      />
      <OldCtasWrapper
        :show-add-to-cart-btn="showAddToCartBtn"
        :add-to-cart-text="addToCartText"
        :add-to-cart-href="addToCartHref"
        :show-dealer-btn="showDealerAction"
        :dealer-text="dealerActionText"
        :dealer-href="dealerActionHref"
        :express-warning="expressWarning"
        :show-express-warning="showExpressWarning"
      />
      <ApacWidget
        v-if="showAPACWidget"
        id="js__product__info__apac"
        :key="partNumber"
      />
      <OldProductFootnote
        :product-header-details="productHeaderDetails"
        :show-product-header-details="showProductHeaderDetails"
        :product-legal-text="productLegalText"
        :show-product-legal-text="showProductLegalText"
        :product-marketing-summary="productMarketingSummary"
        :show-product-marketing-summary="showProductMarketingSummary"
        :part-number="partNumber"
      />
    </template>
    <template v-else>
      <ProductInfoSkeletonLoader />
    </template>
  </div>
</template>

<style lang="scss">
.app__product {

  &__info {
    @extend %product-section;
    flex-basis: auto;
    display: block;
    max-width: 100%;
    text-align: left;
    margin-bottom: 1rem;

    @include productBreakpoint('desktop-s') {
      flex-basis: auto;
      max-width: 50vw;
      padding-right: 7vw;
    }

    @include productBreakpoint('desktop-l') {
      padding-right: 10vw;
    }
  }
}
</style>
